import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { cn } from '@/lib/utils';
import { findFirstFocusableElement, trapFocusWithinOverlay } from './focusTrap';

interface ModalOverlayProps {
  onRequestClose: () => void;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  variant?: 'default' | 'sheet';
}

export function ModalOverlay({
  onRequestClose,
  children,
  className,
  ariaLabel,
  initialFocusRef,
  variant = 'default',
}: ModalOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(
    function manageFocusAndBodyScroll() {
      previouslyFocusedElementRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const overlay = overlayRef.current;
      const requestedInitialFocus = initialFocusRef === undefined ? null : initialFocusRef.current;
      if (requestedInitialFocus !== null) {
        requestedInitialFocus.focus();
      } else if (overlay !== null) {
        const firstFocusable = findFirstFocusableElement(overlay);
        if (firstFocusable !== null) {
          firstFocusable.focus();
        } else {
          overlay.focus();
        }
      }
      return function restoreFocusAndBodyScroll() {
        document.body.style.overflow = previousBodyOverflow;
        const previouslyFocusedElement = previouslyFocusedElementRef.current;
        if (previouslyFocusedElement !== null) {
          previouslyFocusedElement.focus();
        }
      };
    },
    [initialFocusRef]
  );

  useEffect(
    function handleKeyboardInteractions() {
      function handleKeyDown(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          onRequestClose();
          return;
        }
        if (keyboardEvent.key === 'Tab') {
          trapFocusWithinOverlay(overlayRef.current, keyboardEvent);
        }
      }
      window.addEventListener('keydown', handleKeyDown);
      return function removeKeyDownListener() {
        window.removeEventListener('keydown', handleKeyDown);
      };
    },
    [onRequestClose]
  );

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      tabIndex={-1}
      className={cn(
        'fixed inset-0 bg-zooindigo bg-opacity-50 backdrop-blur-sm z-50 overflow-y-auto flex justify-center outline-none',
        variant === 'sheet'
          ? 'items-end sm:items-center p-0 sm:p-4'
          : 'items-start sm:items-center p-4',
        className
      )}
      onMouseDown={function closeWhenBackdropClicked(mouseEvent) {
        if (mouseEvent.target === mouseEvent.currentTarget) {
          onRequestClose();
        }
      }}
    >
      {children}
    </div>
  );
}
